import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, CheckCircle, XCircle, CalendarPlus, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

const InterviewResponsePage = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action") as "confirm" | "alternative" | null;

  const [action, setAction] = useState<"confirm" | "alternative" | "decline" | null>(initialAction);
  const [altDate, setAltDate] = useState<Date>();
  const [altTime, setAltTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responseStatus, setResponseStatus] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<{ name?: string; logo_url?: string; street?: string; house_number?: string; postal_code?: string; city?: string } | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      const { data } = await supabase.rpc('get_public_company_info');
      if (data) setCompanyInfo(data as any);
    };
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    if (initialAction === "confirm" && !submitted && !loading) {
      const confirmAction = async () => {
        try {
          await handleSubmit("confirm");
        } catch (error) {
          console.error("Error confirming interview:", error);
          setError("Ein Fehler ist beim Bestätigen aufgetreten. Bitte versuchen Sie es erneut.");
        }
      };
      confirmAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction]);

  const handleSubmit = async (selectedAction: "confirm" | "alternative" | "decline") => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const body: Record<string, any> = {
        token,
        action: selectedAction,
      };

      if (selectedAction === "alternative") {
        if (!altDate) {
          setError("Bitte wählen Sie ein alternatives Datum aus.");
          setLoading(false);
          return;
        }
        body.alternativeDate = format(altDate, "yyyy-MM-dd");
        body.alternativeTime = altTime;
      }

      if (notes) {
        body.notes = notes;
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/respond-interview-invitation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Ein Fehler ist aufgetreten.");
      }

      setResponseStatus(data.status);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Ein Fehler ist aufgetreten.");
    } finally {
      setLoading(false);
    }
  };

  const companyName = companyInfo?.name || "FlashTec Solution GmbH";
  const companyLogo = companyInfo?.logo_url || "/images/flashtec-logo.jpg";

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header with Logo */}
          <div className="bg-[#1B4D5C] p-5 flex items-center gap-3">
            {companyLogo && (
              <img src={companyLogo} alt={companyName} className="h-16 max-w-[280px] object-contain brightness-0 invert" />
            )}
            {!companyLogo && <span className="text-white font-bold text-lg">{companyName}</span>}
          </div>

          <div className="p-8 text-center" style={{ backgroundColor: '#ffffff', color: '#1e293b' }}>
            {responseStatus === "confirmed" ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#16a34a' }} />
                <h1 className="text-2xl font-bold mb-3" style={{ color: '#1e293b' }}>Vielen Dank für Ihre Bestätigung!</h1>
                <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #16a34a' }}>
                  <p className="text-base mb-2" style={{ color: '#1e293b' }}>
                    Wir freuen uns sehr, Sie persönlich kennenzulernen! Ihr Vorstellungsgespräch ist damit fest eingeplant.
                  </p>
                  <p className="text-base" style={{ color: '#1e293b' }}>
                    Unser Team erwartet Sie mit Vorfreude und wünscht Ihnen bis dahin alles Gute.
                  </p>
                </div>
                <p className="text-sm" style={{ color: '#64748b' }}>
                  Sollten sich bis zum Termin Änderungen ergeben, kontaktieren Sie uns bitte rechtzeitig unter den unten angegebenen Kontaktdaten.
                </p>
              </>
            ) : responseStatus === "alternative_proposed" ? (
              <>
                <CalendarPlus className="h-16 w-16 mx-auto mb-4" style={{ color: '#f59e0b' }} />
                <h1 className="text-2xl font-bold mb-3" style={{ color: '#1e293b' }}>Alternativtermin eingereicht</h1>
                <p className="text-base" style={{ color: '#475569' }}>
                  Vielen Dank für Ihren Vorschlag! Wir haben Ihren Wunschtermin erhalten und melden uns in Kürze bei Ihnen.
                </p>
              </>
            ) : (
              <>
                <XCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#dc2626' }} />
                <h1 className="text-2xl font-bold mb-3" style={{ color: '#1e293b' }}>Termin abgelehnt</h1>
                <p className="text-base" style={{ color: '#475569' }}>
                  Vielen Dank für Ihre Rückmeldung. Wir bedauern, dass der Termin nicht passt, und wünschen Ihnen alles Gute.
                </p>
              </>
            )}
          </div>

          {/* Footer with company info */}
          <div className="bg-slate-800 p-4 text-center">
            <div className="bg-white/90 rounded-md px-3 py-2 inline-block mb-2">
              <img src={companyLogo} alt={companyName} className="h-12 max-w-[200px] object-contain mx-auto" />
            </div>
            <p className="text-slate-300 text-xs font-medium">{companyName}</p>
            <p className="text-slate-400 text-xs mt-1">
              {companyInfo?.street ? `${companyInfo.street} ${companyInfo.house_number}, ${companyInfo.postal_code} ${companyInfo.city}` : "FlashTec Solution GmbH"}
            </p>
            <p className="text-slate-400 text-xs mt-1">
              <a href="mailto:bewerbung@flashtec-solution.de" className="text-slate-400 hover:text-slate-200">bewerbung@flashtec-solution.de</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-[#1B4D5C] text-white p-5 flex items-center gap-3">
          {companyLogo && (
            <img src={companyLogo} alt={companyName} className="h-16 max-w-[280px] object-contain brightness-0 invert" />
          )}
          <h1 className="text-lg font-bold">{!companyLogo ? companyName + " – " : ""}Terminantwort</h1>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Option buttons if no action selected yet */}
          {!action && (
            <div className="space-y-4">
              <p className="text-slate-600 text-center">
                Bitte wählen Sie eine der folgenden Optionen:
              </p>
              <div className="grid gap-3">
                <Button
                  onClick={() => handleSubmit("confirm")}
                  disabled={loading}
                  className="w-full h-14 text-base bg-green-600 hover:bg-green-700"
                >
                  {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle className="h-5 w-5 mr-2" />}
                  Termin bestätigen
                </Button>
                <Button
                  onClick={() => setAction("alternative")}
                  variant="outline"
                  className="w-full h-14 text-base border-amber-400 text-amber-700 hover:bg-amber-50"
                >
                  <CalendarPlus className="h-5 w-5 mr-2" />
                  Alternativen Termin vorschlagen
                </Button>
                <Button
                  onClick={() => setAction("decline")}
                  variant="outline"
                  className="w-full h-14 text-base border-red-300 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  Termin absagen
                </Button>
              </div>
            </div>
          )}

          {/* Auto-confirm loading state */}
          {action === "confirm" && (
            <div className="text-center py-8 space-y-4">
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary" />
              <p className="text-lg font-medium text-foreground">Ihr Termin wird bestätigt...</p>
              <p className="text-sm text-muted-foreground">Bitte warten Sie einen Moment.</p>
            </div>
          )}

          {/* Alternative date form */}
          {action === "alternative" && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg text-slate-800">Alternativen Termin vorschlagen</h2>

              <div className="space-y-2">
                <Label>Gewünschtes Datum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !altDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {altDate ? format(altDate, "PPP", { locale: de }) : "Datum auswählen"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={altDate}
                      onSelect={setAltDate}
                      locale={de}
                      disabled={(d) => d < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Gewünschte Uhrzeit</Label>
                <Input type="time" value={altTime} onChange={(e) => setAltTime(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>Nachricht (optional)</Label>
                <Textarea
                  placeholder="z.B. Ich wäre an diesem Datum verfügbar..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setAction(null)} className="flex-1">
                  Zurück
                </Button>
                <Button
                  onClick={() => handleSubmit("alternative")}
                  disabled={loading || !altDate}
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Vorschlag senden
                </Button>
              </div>
            </div>
          )}

          {/* Decline form */}
          {action === "decline" && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg text-slate-800">Termin absagen</h2>
              <p className="text-slate-600 text-sm">Schade, dass der Termin nicht passt. Möchten Sie uns einen Grund mitteilen?</p>

              <div className="space-y-2">
                <Label>Grund (optional)</Label>
                <Textarea
                  placeholder="z.B. Ich stehe leider nicht mehr zur Verfügung..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setAction(null)} className="flex-1">
                  Zurück
                </Button>
                <Button
                  onClick={() => handleSubmit("decline")}
                  disabled={loading}
                  variant="destructive"
                  className="flex-1"
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Absage senden
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-slate-800 p-4 text-center">
          <div className="bg-white/90 rounded-md px-3 py-2 inline-block mb-2">
            <img src={companyLogo} alt={companyName} className="h-12 max-w-[200px] object-contain mx-auto" />
          </div>
          <p className="text-slate-300 text-xs font-medium">{companyName}</p>
          <p className="text-slate-400 text-xs mt-1">
            {companyInfo?.street ? `${companyInfo.street} ${companyInfo.house_number}, ${companyInfo.postal_code} ${companyInfo.city}` : "FlashTec Solution GmbH"}
          </p>
          <p className="text-slate-400 text-xs mt-1">
            <a href="mailto:bewerbung@flashtec-solution.de" className="text-slate-400 hover:text-slate-200">bewerbung@flashtec-solution.de</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default InterviewResponsePage;
